import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import BenchlingConfig from 'BenchlingConfig';
import UploadRawTextForm from 'bulk-import/panes/UploadRawTextForm';
import * as SampleConstants from 'constants/SampleConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {Thunk} from 'redux-stores/Store';
import ContainerSchemaSelector from 'samples/ContainerSchemaSelector';
import {ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import {StorableSourceFormGroup} from 'samples/storables/StorableSourceSelector';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import InformationalBanner from 'ui-library/banners/InformationalBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {TextAreaChangeEvent} from 'util/Types';

import {
  changeContainerSchema,
  changeContainerSource,
  changeText,
  UploadContainersChangeContainerSchemaAction,
  UploadContainersChangeContainerSourceAction,
  UploadContainersChangeTextAction,
  validateUploadFromPlateMap,
} from './UploadContainersReduxActions';

interface ConnectedProps {
  inputTextError: React.ReactNode | null;
  schemaError: string | null | undefined;
  projectError: string | undefined;
  isValidating: boolean;
  rawText: string;
  selectedContainerSchemaId: string | null;
  selectedContainerSourceId: string | undefined;
}

function mapStateToProps(state: BenchlingState, props: BaseMatrixInputModalProps): ConnectedProps {
  const {
    inputTextError,
    schemaError,
    projectError,
    isValidating,
    rawText,
    selectedContainerSchemaId,
    selectedContainerSourceId,
  } = state.ui.samples.uploadContainersFlow;
  return {
    inputTextError,
    schemaError,
    projectError,
    isValidating,
    rawText,
    selectedContainerSchemaId,
    selectedContainerSourceId,
  };
}

interface BaseMatrixInputModalProps {
  grid: SampleConstants.Grid;
  onRequestClose: () => void;
  title: string;
}

interface DispatchProps {
  onChangeContainerSchema: typeof changeContainerSchema;
  onChangeContainerSource: typeof changeContainerSource;
  onChangeText: typeof changeText;
  onValidateUploadFromPlateMap: (gridId: string, registryId: string) => Thunk<void>;
}

export type MatrixInputModalProps = BaseMatrixInputModalProps & ConnectedProps & DispatchProps;

export class MatrixInputModal extends React.Component<MatrixInputModalProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    grid: SampleConstants.GRID_SHAPE.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,

    // Props from store
    inputTextError: PropTypes.string,
    isValidating: PropTypes.bool.isRequired,
    rawText: PropTypes.string.isRequired,
    schemaError: PropTypes.string,
    projectError: PropTypes.string,
    selectedContainerSchemaId: PropTypes.string,
    selectedContainerSourceId: PropTypes.string,

    onChangeContainerSchema: PropTypes.func.isRequired,
    onChangeContainerSource: PropTypes.func.isRequired,
    onChangeText: PropTypes.func.isRequired,
    onValidateUploadFromPlateMap: PropTypes.func.isRequired,
  };

  componentDidMount(): void {
    this.props.onChangeContainerSchema(this.props.grid.storableSchema.containerSchemaId);
    const defaultSource = BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES
      ? this.props.grid.sourceId
      : this.props.grid.storableSchema.registryId;
    this.props.onChangeContainerSource(defaultSource);
  }

  private handleClose = (): void => {
    this.props.onRequestClose();
  };

  private handleChangeText = (e: TextAreaChangeEvent): UploadContainersChangeTextAction =>
    this.props.onChangeText(e.target.value);

  private handleChangeContainerSchema = (
    selectedContainerSchema: ContainerSchema
  ): UploadContainersChangeContainerSchemaAction =>
    this.props.onChangeContainerSchema(selectedContainerSchema.id);

  private handleChangeContainerSource = (selectedSourceId: {
    sourceId: string;
  }): UploadContainersChangeContainerSourceAction =>
    this.props.onChangeContainerSource(selectedSourceId.sourceId);

  private handleOnClick = (): Thunk<void> =>
    this.props.onValidateUploadFromPlateMap(this.props.grid.id, this.props.grid.storableSchema.registryId);

  render(): JSX.Element {
    return (
      <Modal
        buttons={[
          {
            disabled: this.props.isValidating,
            onClick: this.handleOnClick,
            style: ButtonStyle.SECONDARY,
            text: this.props.isValidating ? 'Validating...' : 'Next',
            dataTestAttrs: {component: 'MatrixInputModal', key: ModalButtonKey.NEXT},
          },
        ]}
        closeableOnEscapeKey={false}
        onRequestClose={this.handleClose}
        title={this.props.title}
      >
        <UploadRawTextForm
          autoFocus={true}
          error={this.props.inputTextError}
          exampleImageURL="/static/img/spreadsheet-import-example-fill-box.png"
          helpText={
            <span>
              <p>
                Text should be in a 2D map format, where each comma- or tab-separated value is the barcode of
                a container that will be added to that position.
              </p>
              <p>
                Each row and column must have the same number of values. Gaps should be marked as "No tube",
                "No read", "No code", or "Empty" (case-insensitive).
              </p>
            </span>
          }
          label="Scan data"
          onTextAreaChange={this.handleChangeText}
          text={this.props.rawText}
        />
        {!this.props.grid.storableSchema.containerSchemaId && (
          <FormGroup>
            <FieldLabel isRequired={true} label="Container Schema" labelStyle={FieldLabel.STYLES.LABEL} />
            <ContainerSchemaSelector
              error={this.props.schemaError}
              onChange={this.handleChangeContainerSchema}
              registryId={this.props.grid.storableSchema.registryId}
              selectedContainerSchemaId={this.props.selectedContainerSchemaId}
              shouldShowNoSchemaOption={false}
            />
          </FormGroup>
        )}
        <StorableSourceFormGroup
          error={this.props.projectError}
          onStorableChange={this.handleChangeContainerSource}
          registryId={this.props.grid.storableSchema.registryId}
          selectedSourceId={this.props.selectedContainerSourceId ?? null}
        />
        <InformationalBanner>
          Only new containers will be moved to the project folder you’ve selected. Any existing containers
          will not have their project location changed.
        </InformationalBanner>
      </Modal>
    );
  }
}

export default connect(mapStateToProps, {
  onChangeText: changeText,
  onChangeContainerSchema: changeContainerSchema,
  onChangeContainerSource: changeContainerSource,
  onValidateUploadFromPlateMap: validateUploadFromPlateMap,
})(MatrixInputModal);
